import { useState } from "react";
import { useAuth, formatErr } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { Rocket } from "lucide-react";

export function AuthPage() {
  const { login, register } = useAuth();
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "login") {
        await login(email, password);
        toast.success("Welcome back");
      } else {
        await register(email, password, name, phone);
        toast.success("Account created · 30-day free trial activated");
      }
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-zinc-100 grid place-items-center p-4">
      <Card className="bg-zinc-950/80 border-zinc-800 p-6 sm:p-8 w-full max-w-md">
        <div className="flex items-center gap-3 mb-6">
          <div className="h-10 w-10 rounded bg-gradient-to-br from-amber-400 to-orange-600 grid place-items-center font-black text-black text-lg">R</div>
          <div>
            <div className="text-xl font-bold">Rachi Algo Console</div>
            <div className="text-[10px] text-zinc-500 font-mono uppercase tracking-widest">NSE · BSE · MCX · Super Orders</div>
          </div>
        </div>

        <div className="mb-3 rounded-lg border border-amber-900/50 bg-amber-950/30 px-3 py-2.5 text-[11px] leading-relaxed text-zinc-300" data-testid="dhan-join-banner">
          <div className="font-semibold text-amber-300 mb-1">Dhan trading account</div>
          <p className="text-zinc-400 mb-1.5">₹0 account opening · ₹0 AMC · Fast API. Get 2 free premium TradingView indicators on signup. Paper mode is free on this platform.</p>
          <a href="https://invite.dhan.co/?join=SUS165" target="_blank" rel="noopener noreferrer" className="text-amber-400 hover:text-amber-300 underline underline-offset-2 font-mono">Open Dhan →</a>
        </div>

        <div className="mb-5 rounded-lg border border-emerald-900/50 bg-emerald-950/25 px-3 py-2.5 text-[11px] leading-relaxed text-zinc-300" data-testid="vps-banner">
          <div className="font-semibold text-emerald-300 mb-1">VPS for LIVE (own IP)</div>
          <p className="text-zinc-400 mb-1.5">Dhan requires IP whitelist for LIVE. Cheap & stable VPS from ~$3/mo — ideal for algo trading.</p>
          <a href="https://www.interserver.net/r/1142633" target="_blank" rel="noopener noreferrer" className="text-emerald-400 hover:text-emerald-300 underline underline-offset-2 font-mono">Get InterServer VPS →</a>
        </div>

        <div className="flex gap-2 mb-5">
          <button data-testid="tab-login" onClick={() => setMode("login")} className={`flex-1 py-2 text-sm font-mono uppercase tracking-wider rounded transition ${mode === "login" ? "bg-amber-500 text-black" : "bg-zinc-900 text-zinc-400 hover:bg-zinc-800"}`}>Sign In</button>
          <button data-testid="tab-signup" onClick={() => setMode("signup")} className={`flex-1 py-2 text-sm font-mono uppercase tracking-wider rounded transition ${mode === "signup" ? "bg-amber-500 text-black" : "bg-zinc-900 text-zinc-400 hover:bg-zinc-800"}`}>Sign Up</button>
        </div>
        <form onSubmit={submit} className="space-y-3">
          {mode === "signup" && (
            <>
              <div className="space-y-1.5"><Label className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-mono">Name</Label><Input data-testid="input-name" value={name} onChange={(e) => setName(e.target.value)} className="bg-zinc-900 border-zinc-800" /></div>
              <div className="space-y-1.5"><Label className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-mono">Phone (optional)</Label><Input data-testid="input-phone" value={phone} onChange={(e) => setPhone(e.target.value)} className="bg-zinc-900 border-zinc-800" /></div>
            </>
          )}
          <div className="space-y-1.5"><Label className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-mono">Email</Label><Input data-testid="input-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="bg-zinc-900 border-zinc-800" /></div>
          <div className="space-y-1.5"><Label className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-mono">Password</Label><Input data-testid="input-password" type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} className="bg-zinc-900 border-zinc-800" /></div>
          <Button data-testid="auth-submit" type="submit" disabled={busy} className="w-full bg-amber-500 hover:bg-amber-400 text-black font-bold mt-4 h-11">
            <Rocket className="h-4 w-4 mr-2" /> {busy ? "Please wait…" : mode === "login" ? "Sign In" : "Create Account · Start Free Trial"}
          </Button>
        </form>
        <div className="mt-5 text-[11px] text-zinc-500 font-mono text-center">
          {mode === "signup" ? "30-day free trial · No card required · Cancel anytime" : "Don't have an account? Click Sign Up · ₹3000/month after trial"}
        </div>
      </Card>
    </div>
  );
}
